import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, AlertCircle, Loader } from 'lucide-react';
import { Sale } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';
import { productReturnService } from '../services/productReturnService';
import { customToast } from '../utils/customToast';

interface ReturnRequestModalProps {
  sale: Sale | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ReturnRequestModal: React.FC<ReturnRequestModalProps> = ({ sale, isOpen, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<{ [key: number]: number }>({});
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [returnType, setReturnType] = useState<'partial' | 'full'>('partial');

  // Initialize data loading when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsDataLoading(true);
      // Simulate data load - since we get sale data as prop, resolve immediately
      const timer = setTimeout(() => setIsDataLoading(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Debug log effect - hooks must be before early returns
  useEffect(() => {
    console.log('ReturnRequestModal - Sale data:', {
      saleId: sale?.id,
      hasSaleItems: !!sale?.saleItems,
      saleItemsLength: sale?.saleItems?.length,
      saleItemsData: sale?.saleItems
    });
  }, [sale]);

  // Auto-select all items when return type is set to full
  useEffect(() => {
    if (returnType === 'full' && sale?.saleItems && sale.saleItems.length > 0) {
      const allItems: { [key: number]: number } = {};
      sale.saleItems.forEach(item => {
        allItems[item.id] = item.quantity;
      });
      setSelectedItems(allItems);
    } else if (returnType === 'partial') {
      setSelectedItems({});
    }
  }, [returnType, sale?.saleItems]);

  if (!isOpen && !isClosing) return null;

  const handleItemQuantityChange = (saleItemId: number, quantity: number) => {
    const maxQty = sale?.saleItems?.find(item => item.id === saleItemId)?.quantity || 0;
    if (quantity > 0 && quantity <= maxQty) {
      setSelectedItems(prev => ({
        ...prev,
        [saleItemId]: quantity
      }));
    } else if (quantity === 0) {
      setSelectedItems(prev => {
        const newState = { ...prev };
        delete newState[saleItemId];
        return newState;
      });
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setSelectedItems({});
      setReason('');
      setNotes('');
      setReturnType('partial');
      onClose();
    }, 150);
  };

  const handleSubmit = async () => {
    if (!sale) return;
    
    if (Object.keys(selectedItems).length === 0) {
      customToast.error('Please select at least one item to return');
      return;
    }

    if (!reason.trim()) {
      customToast.error('Please provide a reason for the return');
      return;
    }

    try {
      setLoading(true);
      const returnData = {
        sale_id: sale.id,
        reason: reason.trim(),
        items: Object.entries(selectedItems).map(([saleItemId, quantity]) => {
          const item = sale.saleItems?.find(i => i.id === Number(saleItemId));
          return {
            sale_item_id: Number(saleItemId),
            quantity_returned: quantity,
            return_reason: reason.trim(),
            condition: 'good' as const,
            product_id: item?.product_id
          };
        }),
        notes: notes.trim()
      };

      const response = await productReturnService.createReturn(returnData);
      
      customToast.success(`Return #${response.return_number} created successfully`);
      
      // Show additional message if full return
      if ((response as any).is_full_return) {
        customToast.info('Full return processed. Sale has been adjusted to reflect the return.');
      }
      
      // Await onSuccess callback before closing
      await onSuccess();
      handleClose();
    } catch (error) {
      customToast.error(error instanceof Error ? error.message : 'Failed to create return');
    } finally {
      setLoading(false);
    }
  };

  const totalReturnAmount = Object.entries(selectedItems).reduce((sum, [saleItemId, qty]) => {
    const item = sale?.saleItems?.find(i => i.id === Number(saleItemId));
    return sum + ((item?.unit_price || 0) * qty);
  }, 0);

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
          isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'}`}>
                <RotateCcw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                  Create Product Return
                </h2>
                <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Sale #{sale?.shop_display_id || sale?.id}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
          {isDataLoading ? (
            // Loading Skeleton
            <div className="p-4 sm:p-5 md:p-6 space-y-6">
              {/* Return Type Section Skeleton */}
              <div className="space-y-3">
                <div className={`h-5 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className="grid grid-cols-2 gap-3">
                  {[...Array(2)].map((_, idx) => (
                    <div key={idx} className={`h-16 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                  ))}
                </div>
              </div>

              {/* Sale Info Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[...Array(2)].map((_, idx) => (
                  <div key={idx} className={`p-3 rounded-lg space-y-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <div className={`h-4 w-24 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                    <div className={`h-4 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                  </div>
                ))}
              </div>

              {/* Items Section Skeleton */}
              <div className="space-y-3">
                <div className={`h-5 w-40 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className="space-y-2">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className={`h-20 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-5 md:p-6">
            {/* Return Type Selection */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                Return Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setReturnType('partial')}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    returnType === 'partial'
                      ? theme === 'dark'
                        ? 'border-indigo-500 bg-indigo-900/30'
                        : 'border-indigo-600 bg-indigo-50'
                      : theme === 'dark'
                      ? 'border-gray-600 bg-gray-700/50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <p className={`font-medium text-sm ${
                    returnType === 'partial'
                      ? theme === 'dark'
                        ? 'text-indigo-300'
                        : 'text-indigo-700'
                      : theme === 'dark'
                      ? 'text-gray-200'
                      : 'text-slate-900'
                  }`}>
                    Partial Return
                  </p>
                  <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Return specific items</p>
                </button>
                <button
                  onClick={() => setReturnType('full')}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    returnType === 'full'
                      ? theme === 'dark'
                        ? 'border-indigo-500 bg-indigo-900/30'
                        : 'border-indigo-600 bg-indigo-50'
                      : theme === 'dark'
                      ? 'border-gray-600 bg-gray-700/50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <p className={`font-medium text-sm ${
                    returnType === 'full'
                      ? theme === 'dark'
                        ? 'text-indigo-300'
                        : 'text-indigo-700'
                      : theme === 'dark'
                      ? 'text-gray-200'
                      : 'text-slate-900'
                  }`}>
                    Full Return
                  </p>
                  <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Return entire sale</p>
                </button>
              </div>
            </div>

            {/* Sale Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4 mb-3 sm:mb-4">
              <div className={`p-2.5 sm:p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-2 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Sale Information
                </h4>
                <div className="space-y-2">
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Customer</p>
                    <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {sale?.customer?.name || sale?.customer_name || 'Walk-in'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Amount</p>
                    <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(Number(sale?.total_amount || 0))}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-2.5 sm:p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-2 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Details
                </h4>
                <div className="space-y-2">
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Items Count</p>
                    <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {sale?.saleItems?.length || 0} items
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Date</p>
                    <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {new Date(sale?.created_at || '').toLocaleDateString('en-PK')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Select Items */}
            <div className="mb-4">
              <h3 className={`font-medium mb-3 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Select Items to Return
              </h3>
              
              {sale?.saleItems && sale.saleItems.length > 0 ? (
                <div className="space-y-2">
                  {sale.saleItems.map((item) => (
                    <div
                      key={item.id}
                      className={`p-2.5 sm:p-3 rounded-lg border transition-colors ${
                        selectedItems[item.id]
                          ? theme === 'dark'
                            ? 'bg-indigo-900/30 border-indigo-600/50'
                            : 'bg-indigo-50 border-indigo-300'
                          : theme === 'dark'
                            ? 'bg-gray-700/50 border-gray-600'
                            : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0 pr-3">
                          <p className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {item.product_name}
                          </p>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {formatCurrency(Number(item.unit_price))} × {item.quantity} = {formatCurrency(Number(item.total_price))}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <label className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {returnType === 'full' ? 'Full Return' : 'Qty to Return:'}
                        </label>
                        {returnType === 'full' ? (
                          <span className={`w-16 px-2 py-1 text-sm text-center font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {item.quantity}
                          </span>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            max={item.quantity}
                            value={selectedItems[item.id] || 0}
                            onChange={(e) => handleItemQuantityChange(item.id, parseInt(e.target.value) || 0)}
                            className={`w-16 px-2 py-1 text-sm rounded-md border text-center font-semibold focus:outline-none focus:ring-0 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`p-3 rounded-lg flex items-center space-x-2 ${
                  theme === 'dark' ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-600'
                }`}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">No items available to return</p>
                </div>
              )}
            </div>

            {/* Return Reason */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Reason for Return <span className="text-red-500">*</span>
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-0 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Select a reason...</option>
                <option value="damaged">Damaged/Defective</option>
                <option value="wrong_item">Wrong Item Sold</option>
                <option value="customer_request">Customer Request</option>
                <option value="expired">Expired/Expired Soon</option>
                <option value="quality_issue">Quality Issue</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Additional Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter any additional details about the return..."
                rows={3}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-0 resize-none ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            {/* Return Amount Summary */}
            {Object.keys(selectedItems).length > 0 && (
              <div className={`p-3 rounded-lg mb-4 border-l-4 ${
                theme === 'dark'
                  ? 'bg-gray-700/50 border-indigo-500'
                  : 'bg-indigo-50 border-indigo-500'
              }`}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Total Return Amount:
                  </p>
                  <p className={`text-lg font-bold ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {formatCurrency(totalReturnAmount)}
                  </p>
                </div>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 flex items-center justify-end gap-2 sm:gap-3 rounded-b-xl ${
          theme === 'dark' ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-gray-50/95'
        }`}>
          <button
            onClick={handleClose}
            disabled={loading}
            className={`px-4 sm:px-5 py-2 sm:py-2.5 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700 disabled:opacity-50'
                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || Object.keys(selectedItems).length === 0 || !reason.trim()}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-sm font-medium rounded-lg text-white transition-all touch-manipulation ${
              loading || Object.keys(selectedItems).length === 0 || !reason.trim()
                ? 'bg-gray-400 opacity-60 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg'
            }`}
          >
            {loading && <Loader className="w-4 h-4 animate-spin" />}
            {loading ? 'Creating...' : 'Create Return'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReturnRequestModal;
